/* Cap customizer — cart-page/drawer quantity sync.

   WHY THIS EXISTS. A configured build is several independent Shopify cart
   lines (garment/colourway line(s), decoration line(s), setup line(s)),
   linked only by a shared `_build` line-item property -- see
   wce-cart-functions/docs/decoration-cart-integrity-plan.md for the full
   picture. The cart/checkout validation functions are the real backstop
   (they block checkout on a broken build); this script is the smooth path
   that keeps a build from ever LOOKING broken in the first place, by
   keeping quantities in lockstep when a customer edits them on THIS site's
   cart page or drawer.

   WHAT THIS CANNOT REACH. Shopify's own hosted checkout has its own native
   quantity stepper, and nothing running on wholesale-caps.com can touch
   that page. A customer who edits quantity there, or anyone hitting the
   cart API directly, bypasses this script entirely -- which is exactly why
   the validation functions exist independently of it. This script is a
   convenience, not a security boundary.

   HOW A LINE IS IDENTIFIED. `data-build`, `data-role` (garment | decoration
   | setup) and `data-key` are stamped on each `.cart-item` row by
   sections/main-cart-items.liquid and snippets/cart-drawer.liquid, keyed
   off the `_parent` line-item property (`cap-config` on every decoration/
   setup line, absent on garment lines) -- the same signal the validation
   functions read server-side. NOT product handle: there isn't one
   "cap-customizations" companion product, and a handle check only ever
   covers whichever template it was tested against (see
   wce-cart-functions/docs/decoration-cart-integrity-plan.md, "every
   non-cap order broken" incident, 2026-09-07). A row with no `data-build`
   is not part of a configured build and this script never touches it.

   EVERY UPDATE IS ONE ATOMIC `/cart/update.js` CALL. Earlier drafts issued
   a separate `/cart/change.js` per line and ran them in a chosen order to
   dodge the validation functions -- fragile, because ANY sequence of
   separate requests exposes an intermediate state in between them, and the
   validation runs on every single mutation. `/cart/update.js` accepts a
   `updates: {lineKey: quantity, ...}` map and applies it as ONE cart
   mutation (verified directly against this store first), so the
   validation only ever sees the fully-consistent before or after state --
   never a half-applied one. This is the same lesson the add-to-cart chain
   fix learned the hard way; no reason to relearn it here.

   DECORATION IS PER LOCATION, NOT SUMMED ACROSS LOCATIONS. Each decoration
   line (front, back, a 3D puff add-on, ...) independently carries the
   FULL garment quantity -- verified against `P.addonItems` in
   cap-customizer.js, and against the matching fix in the validation
   function's own history. So every decoration line in a build gets set to
   the SAME target number, never a share of it and never summed together.

   MULTI-COLOURWAY BUILDS ARE REAL. Ordering several colourways to hit a
   quantity tier is a documented storefront flow. Garment quantity is the
   SUM across every colourway line in the build; decoration mirrors that
   sum, not any single colourway line. */

(function () {
  if (window.__ccCartSyncLoaded) return;
  window.__ccCartSyncLoaded = true;

  /* The cart drawer renders globally (it lives in the page header markup),
     so its rows exist in the DOM at the same time as the standalone cart
     page's rows -- every real line appears TWICE. `preferredRow`, when one
     of the duplicates for a key, wins the collision, so the row whose
     input the customer actually just edited (freshest value) is the one
     whose quantity gets read, not whichever duplicate happened to come
     first in document order. */
  function rowsForBuild(build, preferredRow) {
    var all = Array.prototype.slice.call(
      document.querySelectorAll('.cart-item[data-build="' + CSS.escape(build) + '"]')
    );
    var byKey = new Map();
    all.forEach(function (r) {
      var k = r.dataset.key;
      if (!byKey.has(k) || r === preferredRow) byKey.set(k, r);
    });
    return Array.from(byKey.values());
  }

  function byRole(rows, role) {
    return rows.filter(function (r) { return r.dataset.role === role; });
  }

  function rowQty(row) {
    var input = row.querySelector('.quantity__input');
    var v = input ? parseInt(input.value, 10) : parseInt(row.dataset.qty, 10);
    return isFinite(v) ? v : 0;
  }

  /* One atomic multi-line mutation. `updates` is {lineKey: quantity}. A
     rejection (422) comes back as {status, message, description} rather
     than throwing -- `fetch` only rejects on a network failure, not on a
     non-2xx status -- so callers must check `.error` explicitly instead of
     assuming success. This should be rare now that every sibling line
     moves together in one call, but "rare" is not "impossible": the
     validation function is still the authority on what's allowed, and a
     failed update must not be treated as if it landed. */
  function bulkUpdate(updates) {
    if (!Object.keys(updates).length) return Promise.resolve({});
    return fetch(window.routes.cart_update_url, Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ updates: updates }),
    }))
      .then(function (r) {
        return r.json().then(function (body) {
          return r.ok ? body : { error: body.description || body.message || true };
        });
      })
      .catch(function (e) {
        console.error(e);
        return { error: true };
      });
  }

  /* Re-fetches the cart sections and swaps them in, same shape as
     CartItems.onCartUpdate() in cart.js. Needed because this script's
     update happens outside cart.js's own render cycle. */
  function refreshCartSections() {
    var isDrawer = !!document.querySelector('cart-drawer-items');
    var url = isDrawer
      ? window.routes.cart_url + '?section_id=cart-drawer'
      : window.routes.cart_url + '?section_id=main-cart-items';

    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (isDrawer) {
          ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
            var target = document.querySelector(sel);
            var source = doc.querySelector(sel);
            if (target && source) target.replaceWith(source);
          });
        } else {
          var target = document.querySelector('cart-items');
          var source = doc.querySelector('cart-items');
          if (target && source) target.innerHTML = source.innerHTML;
        }
      })
      .catch(function (e) { console.error(e); });
  }

  function onGarmentQuantityChange(row) {
    var rows = rowsForBuild(row.dataset.build, row);
    var garmentRows = byRole(rows, 'garment');
    var decorationRows = byRole(rows, 'decoration');
    if (!decorationRows.length) return; // blank order, nothing to keep in sync

    var total = garmentRows.reduce(function (n, r) { return n + rowQty(r); }, 0);

    /* The edited garment line's own new value rides in the SAME atomic
       call as every decoration line's new target. cart.js's own competing
       update of that one line is stopped from ever firing (see the
       'change' listener below) -- this call is the ONLY update the edited
       line gets, not a redundant confirmation of one cart.js already
       made. */
    var updates = {};
    updates[row.dataset.key] = rowQty(row);
    decorationRows.forEach(function (r) { updates[r.dataset.key] = total; });

    bulkUpdate(updates).then(function (result) {
      if (result && result.error) {
        console.error('cap-customizer-cart-sync: update rejected -- ' + result.error);
      }
      return refreshCartSections();
    });
  }

  function onDecorationQuantityChange(row) {
    var rows = rowsForBuild(row.dataset.build, row);
    var garmentRows = byRole(rows, 'garment');
    /* More than one colourway makes a decoration-side edit ambiguous --
       which colourway should absorb the change? Don't guess. The
       server-side quantity-mismatch check will catch it if the numbers
       end up disagreeing; this just declines to make it worse. */
    if (garmentRows.length !== 1) return;

    var newQty = rowQty(row);
    var decorationRows = byRole(rows, 'decoration');

    var updates = {};
    decorationRows.forEach(function (r) { updates[r.dataset.key] = newQty; });
    updates[garmentRows[0].dataset.key] = newQty;

    bulkUpdate(updates).then(function (result) {
      if (result && result.error) {
        console.error('cap-customizer-cart-sync: update rejected -- ' + result.error);
      }
      return refreshCartSections();
    });
  }

  /* Capture phase, and stops the event before cart-items' own debounced
     listener (attached in cart.js) ever sees it -- a build-linked
     quantity edit is handled EXCLUSIVELY here, never both here and by
     cart.js's own single-line update racing it.

     That race was real, not hypothetical: cart.js debounces its own
     update by 300ms (ON_CHANGE_DEBOUNCE_TIMER) and updates by line INDEX,
     independently of this script's immediate, key-based atomic update.
     Whichever lands second would occasionally find the sibling side
     already moved (this script having landed first, correctly) or would
     itself land first and trip the mismatch check on the intermediate
     state this script hadn't gotten to yet -- reproduced live, 2026-09-06:
     raising then lowering a colourway's quantity left a stale "quantity
     mismatch" error rendered against an already-consistent cart, from a
     race loser's failed request whose error text nothing ever cleared.
     Taking over the whole update removes the second, competing path
     rather than trying to win a race against it. */
  document.addEventListener('change', function (event) {
    var input = event.target.closest('.quantity__input');
    if (!input) return;
    var row = input.closest('.cart-item');
    if (!row || !row.dataset.build) return;
    if (row.dataset.role !== 'garment' && row.dataset.role !== 'decoration') return;

    event.stopPropagation();

    if (row.dataset.role === 'garment') onGarmentQuantityChange(row);
    else onDecorationQuantityChange(row);
    // setup rows have no quantity control (hidden in the templates above);
    // nothing to listen for there.
  }, true);

  /* Capture phase, and stops the event before cart-remove-button's own
     listener (attached in cart.js) ever runs -- this script controls the
     FULL removal as one atomic call instead of racing a second, separate
     removal of the same line. Decoration/setup rows never render a remove
     button (hidden in the templates above), so this only ever fires for a
     garment row. */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('cart-remove-button');
    if (!btn) return;
    var row = btn.closest('.cart-item');
    if (!row || !row.dataset.build) return; // not a configured build; let the default handler run

    event.preventDefault();
    event.stopPropagation();

    var rows = rowsForBuild(row.dataset.build, row);
    var garmentRows = byRole(rows, 'garment');
    var decorationRows = byRole(rows, 'decoration');
    var setupRows = byRole(rows, 'setup');
    var removedQty = rowQty(row);

    var updates = {};
    updates[row.dataset.key] = 0;

    if (garmentRows.length <= 1) {
      // Only/last colourway: the whole build goes with it.
      decorationRows.forEach(function (r) { updates[r.dataset.key] = 0; });
      setupRows.forEach(function (r) { updates[r.dataset.key] = 0; });
    } else {
      // One of several colourways: shrink the shared decoration lines by
      // this line's quantity. Setup is untouched -- it's per-order, not
      // per-colourway.
      var newTotal = Math.max(
        0,
        garmentRows.reduce(function (n, r) { return n + rowQty(r); }, 0) - removedQty
      );
      decorationRows.forEach(function (r) { updates[r.dataset.key] = newTotal; });
    }

    bulkUpdate(updates).then(function (result) {
      if (result && result.error) {
        console.error('cap-customizer-cart-sync: update rejected -- ' + result.error);
      }
      return refreshCartSections();
    });
  }, true);
})();
